import React from "react";
import { ReactComponent as RequestsMark } from "../../../assets/svg/Dsahboard/star.svg";
import { ReactComponent as PlansMark } from "../../../assets/svg/Dsahboard/plan.svg";
import { ReactComponent as ClientsMark } from "../../../assets/svg/Dsahboard/client.svg";
import { LoadPhase, SkeletonCard } from "../../../components/ui";
import { useSignedInUser } from "../../auth/hooks/useSignedInUser";
import SetupChecklist from "./SetupChecklist";
import type { SetupStep } from "../setupSteps";

/** What a figure the backend has not counted shows in place of a number. */
const NO_FIGURE = "-";

/**
 * The three figures across the card, in the order the design puts them. Each is
 * a count from `GET /api/v1/dashboard/`; what is written here is only how it is
 * labelled and drawn.
 *
 * The marks are the drawn artwork, as on the checklist below: each file carries
 * its own disc and its own stroke colour, so there is no tint to name here and
 * nothing for the card to colour them with.
 */
const HIGHLIGHTS: {
  id: keyof WelcomeCardProps["highlights"];
  label: string;
  note: string;
  Mark: React.ElementType;
}[] = [
  {
    id: "requests",
    label: "new request",
    note: "In last 7 days",
    Mark: RequestsMark,
  },
  {
    id: "plans",
    label: "plan create",
    note: "In last 7 days",
    Mark: PlansMark,
  },
  {
    id: "renewals",
    label: "clients renewal",
    note: "In coming 7 days",
    Mark: ClientsMark,
  },
];

interface WelcomeCardProps {
  /**
   * The week either side of today, from the dashboard endpoint: requests raised
   * and plans created in the last seven days, and the plans running out in the
   * coming seven.
   *
   * Any of them is `null` while it is on its way and again where the backend
   * has no figure for it, and both read as a dash — a "0 new request" nobody
   * has counted is a claim about the week.
   */
  highlights: {
    requests: number | null;
    plans: number | null;
    renewals: number | null;
  };
  /**
   * The workspace's setup, while any of it is outstanding — the card then shows
   * what is left to do instead of figures, because a workspace that has not
   * added a client has no figures to show.
   *
   * `null` once it is all done, and also where it could not be worked out: see
   * `useBrandSetUp`, which is quiet about a failure precisely so the card can
   * fall back to the highlights rather than tick something it is unsure of.
   */
  setup?: SetupStep[] | null;
  /**
   * Where the dashboard call is. The card is not drawn until it answers: which
   * of the two rows belongs here — the checklist or the highlights — is itself
   * something being loaded, and a greeting over a blank space reads as a card
   * that arrived broken. A wait long enough to notice shimmers the whole card.
   */
  phase?: LoadPhase;
}

/**
 * The card's own footprint, so the block that stands in for it is the size of
 * the card that lands in its place: three rows deep on a phone, one row of
 * three from `sm`.
 *
 * A height for the shimmer and nothing else — the card itself is left to its
 * content. The two rows it can hold are not the same depth (the checklist's
 * discs are 48px against the highlights' 40px, and stacked on a phone that
 * difference is three rows' worth), so a floor set deep enough for the taller
 * of them leaves a band of nothing under the shorter one. Beside the stat
 * tiles from `xl` the grid levels the row anyway.
 */
const FOOTPRINT = "h-[252px] sm:h-[164px] 3xl:h-[188px]";

const WelcomeCard: React.FC<WelcomeCardProps> = ({
  highlights,
  setup = null,
  phase = "ready",
}) => {
  const { firstName } = useSignedInUser();

  if (phase !== "ready") {
    return <SkeletonCard phase={phase} className={FOOTPRINT} />;
  }

  return (
    <section
      className="rounded-md border border-line bg-white p-4 sm:p-6 3xl:p-8"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[18px] sm:text-[20px] 3xl:text-[24px] font-semibold text-ink">
            {/* No name while Clerk is still restoring the session, and none for
                the account that carries neither a name nor an email. "Hello,"
                with nothing after it reads as a bug, so the comma goes with the
                name. */}
            Hello{firstName ? `, ${firstName}` : ""}
          </h2>
          <p className="mt-0.5 text-[13px] 3xl:text-[15px] text-ink-muted">
            Welcome to your Naurist workspace
          </p>
        </div>

        {/* Only beside the checklist. Once there is nothing left to set up the
            card shows figures instead, and a pill saying the setup is pending
            over a row of highlights would contradict the row under it. */}
        {setup && (
          <span className="flex-shrink-0 rounded-full px-3 py-1 text-[12px] sm:text-[13px] font-medium bg-[#FFE8D2] text-[#F78317]">
            Setup pending
          </span>
        )}
      </div>

      {/* The checklist where there is anything left to tick, and the three
          highlights once there is not — which of the two belongs here was
          settled by the answer that has already landed above. */}
      {setup ? (
        <SetupChecklist steps={setup} />
      ) : (
        <div className="mt-5 sm:mt-6 grid gap-4 sm:gap-5 sm:grid-cols-3">
          {HIGHLIGHTS.map((item) => {
            const Mark = item.Mark;
            const value = highlights[item.id] ?? NO_FIGURE;
            return (
              <div key={item.id} className="flex items-center gap-3">
                {/* Drawn at 40px rather than the 48px the file is authored at,
                    which is the footprint the row has always had — see
                    `FOOTPRINT`. The disc is inside the artwork, so the mark is
                    the whole thing rather than a glyph over a tinted circle. */}
                <Mark aria-hidden className="w-10 h-10 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-[14px] font-medium text-ink truncate">
                    {value} {item.label}
                  </div>
                  <div className="text-[12px] text-ink-subtle truncate">
                    {item.note}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default WelcomeCard;
